import { useCallback, useEffect, useState } from "react";
import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type {
  AgencyEditorPatchAttemptSummaryV1,
  StudioDepartureKitStatusV1,
  StudioIntakeSummaryV1,
  StudioProjectLifecycleViewV1,
  StudioSafeEditPresentationV1,
} from "@enrein/shared-contracts";
import type { BusinessGraphActiveViewV1 } from "@enrein/shared-contracts/studio-project-workflow";
import type { StudioRunRowV1 } from "@enrein/shared-contracts/studio-dashboard";
import type { StudioApiClient } from "../../../api.js";
import { formatWorkflowError } from "../workflow/studio-workflow.js";
import { refreshProjectSnapshot } from "./project-snapshot.js";

export interface StudioProjectSnapshotState {
  readonly clientReview: ClientReviewStatusV1 | null;
  readonly bgStatus: Awaited<ReturnType<StudioApiClient["getBusinessGraphStatus"]>> | null;
  readonly activeGraph: BusinessGraphActiveViewV1 | null;
  readonly businessName: string | null;
  readonly runs: readonly StudioRunRowV1[];
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly lifecycle: StudioProjectLifecycleViewV1 | null;
  readonly agencyEditorPatchAttempts: readonly AgencyEditorPatchAttemptSummaryV1[];
  readonly safeEditPresentation: StudioSafeEditPresentationV1 | null;
  readonly projectLoading: boolean;
  readonly projectLoadError: string | null;
  readonly reloadSelectedProject: () => Promise<Awaited<ReturnType<typeof refreshProjectSnapshot>> | null>;
}

export function useStudioProjectSnapshot(
  api: StudioApiClient,
  selectedProjectId: string | null,
): StudioProjectSnapshotState {
  const [clientReview, setClientReview] = useState<ClientReviewStatusV1 | null>(null);
  const [bgStatus, setBgStatus] = useState<Awaited<ReturnType<StudioApiClient["getBusinessGraphStatus"]>> | null>(
    null,
  );
  const [activeGraph, setActiveGraph] = useState<BusinessGraphActiveViewV1 | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [runs, setRuns] = useState<readonly StudioRunRowV1[]>([]);
  const [intakeSummary, setIntakeSummary] = useState<StudioIntakeSummaryV1 | null>(null);
  const [departureKit, setDepartureKit] = useState<StudioDepartureKitStatusV1 | null>(null);
  const [lifecycle, setLifecycle] = useState<StudioProjectLifecycleViewV1 | null>(null);
  const [agencyEditorPatchAttempts, setAgencyEditorPatchAttempts] = useState<
    readonly AgencyEditorPatchAttemptSummaryV1[]
  >([]);
  const [safeEditPresentation, setSafeEditPresentation] =
    useState<StudioSafeEditPresentationV1 | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null);

  const applySnapshot = useCallback(
    (snapshot: Awaited<ReturnType<typeof refreshProjectSnapshot>>) => {
      setClientReview(snapshot.review);
      setBgStatus(snapshot.status);
      setRuns(snapshot.runRows);
      setBusinessName(snapshot.businessName);
      setIntakeSummary(snapshot.intakeSummary);
      setActiveGraph(snapshot.activeGraph);
      setDepartureKit(snapshot.departureKit);
      setLifecycle(snapshot.lifecycle);
      setAgencyEditorPatchAttempts(snapshot.agencyEditorPatchAttempts);
      setSafeEditPresentation(snapshot.safeEditPresentation);
    },
    [],
  );

  const reloadSelectedProject = useCallback(async () => {
    if (!selectedProjectId) return null;
    const snapshot = await refreshProjectSnapshot(api, selectedProjectId);
    applySnapshot(snapshot);
    return snapshot;
  }, [api, applySnapshot, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setClientReview(null);
      setBgStatus(null);
      setActiveGraph(null);
      setBusinessName(null);
      setRuns([]);
      setIntakeSummary(null);
      setDepartureKit(null);
      setLifecycle(null);
      setAgencyEditorPatchAttempts([]);
      setSafeEditPresentation(null);
      setProjectLoading(false);
      setProjectLoadError(null);
      return;
    }
    let cancelled = false;
    setProjectLoading(true);
    setProjectLoadError(null);
    void (async () => {
      try {
        const snapshot = await refreshProjectSnapshot(api, selectedProjectId);
        if (!cancelled) applySnapshot(snapshot);
      } catch (error) {
        if (!cancelled) {
          setClientReview(null);
          setBgStatus(null);
          setActiveGraph(null);
          setRuns([]);
          setIntakeSummary(null);
          setAgencyEditorPatchAttempts([]);
          setSafeEditPresentation(null);
          setProjectLoadError(formatWorkflowError(error));
        }
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, applySnapshot, selectedProjectId]);

  return {
    clientReview,
    bgStatus,
    activeGraph,
    businessName,
    runs,
    intakeSummary,
    departureKit,
    lifecycle,
    agencyEditorPatchAttempts,
    safeEditPresentation,
    projectLoading,
    projectLoadError,
    reloadSelectedProject,
  };
}
